import { useCallback, useEffect, useState } from 'react';
import { useActiveConnector } from '../contexts/ConnectorContext';

interface UseReportDataResult<T = Record<string, unknown>> {
    data: T[];
    loading: boolean;
    error: string | null;
    meta?: Record<string, unknown>;
    refetch: () => void;
}

export function useReportData<T = Record<string, unknown>>(
    endpoint: string,
    params: Record<string, unknown> = {},
    enabled = true,
): UseReportDataResult<T> {
    const activeConnector = useActiveConnector();
    const [data, setData] = useState<T[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [meta, setMeta] = useState<Record<string, unknown> | undefined>();
    const [trigger, setTrigger] = useState(0);

    const serializedParams = JSON.stringify(params);
    const connectorId = activeConnector.id;

    const refetch = useCallback(() => setTrigger((t) => t + 1), []);

    useEffect(() => {
        if (!enabled) return;

        const controller = new AbortController();
        setLoading(true);
        setError(null);
        setData([]);
        setMeta(undefined);

        const token = localStorage.getItem('auth_token');
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Connector-Id': String(connectorId),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };

        fetch(endpoint, {
            method: 'POST',
            headers,
            body: serializedParams,
            signal: controller.signal,
        })
            .then((res) => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.json();
            })
            .then((json: { data?: T[]; meta?: Record<string, unknown> }) => {
                setData(json.data ?? []);
                setMeta(json.meta);
                setLoading(false);
            })
            .catch((err) => {
                if (err.name !== 'AbortError') {
                    setError(err.message);
                    setLoading(false);
                }
            });

        return () => controller.abort();
    }, [endpoint, serializedParams, enabled, trigger, connectorId]);

    return { data, loading, error, meta, refetch };
}
